import { Component, ChangeDetectionStrategy, inject, computed, viewChild, ElementRef, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AuthService } from '../services/auth/auth.service';
import { BranchPickerComponent } from '../shared/components/branch-picker.component';
import { SubscriptionBannerComponent } from '../shared/components/subscription-banner.component';
import { SubscriptionService } from '../services/supabase/subscription.service';
import { PermissionService } from '../services/supabase/permission.service';
import { FeatureFlagService } from '../services/feature-flag/feature-flag.service';

interface NavTab {
  label: string;
  // Shorter label used by the mobile bottom nav so long words like
  // "Hospitalisations" don't ellipsis on phone-width tiles.
  mobileLabel?: string;
  icon: string;
  route: string;
  exact: boolean;
  permission?: string;
  // Visible when the user holds ANY of these (for tabs that aggregate features,
  // e.g. the Directory needs clients.view OR patients.view).
  permissionAny?: string[];
  // Extra URL prefixes that also light this tab (the Directory owns the
  // /clients and /patients record pages even though it lives at /directory).
  activePrefixes?: string[];
}

@Component({
  selector: 'app-shell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatProgressBarModule,
    BranchPickerComponent,
    SubscriptionBannerComponent
],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
})
export class ShellComponent {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly subscriptions = inject(SubscriptionService);
  private readonly perms = inject(PermissionService);
  private readonly featureFlags = inject(FeatureFlagService);

  readonly tabs: NavTab[] = [
    { label: 'Home', icon: 'home', route: '/', exact: true },
    { label: 'Calendar', mobileLabel: 'Cal.', icon: 'calendar_month', route: '/calendar', exact: false, permission: 'calendar.view' },
    { label: 'Schedule', mobileLabel: 'Roster', icon: 'calendar_view_week', route: '/schedule', exact: false, permission: 'scheduling.view' },
    { label: 'Directory', mobileLabel: 'Dir.', icon: 'contacts', route: '/directory', exact: false, permissionAny: ['clients.view', 'patients.view'], activePrefixes: ['/clients', '/patients'] },
    {
      label: 'Hospitalisations',
      mobileLabel: 'Hosp.',
      icon: 'local_hospital',
      route: '/hospitalisations',
      exact: false,
      permission: 'hospitalisations.view',
    },
    {
      label: 'Consultations',
      mobileLabel: 'Consult',
      icon: 'assignment',
      route: '/consultations',
      exact: false,
      permission: 'consultations.view',
    },
    { label: 'Medications', mobileLabel: 'Meds', icon: 'medication', route: '/medications', exact: false, permission: 'medications.view' },
  ];

  readonly visibleTabs = computed<NavTab[]>(() =>
    this.tabs.filter(tab => {
      if (tab.permissionAny) return this.perms.hasAny(tab.permissionAny);
      return !tab.permission || this.perms.has(tab.permission);
    }),
  );

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  // Top progress bar: visible while a route transition is in flight (lazy
  // chunk fetch, guards, resolvers). Cleared on End/Cancel/Error.
  readonly navLoading = toSignal(
    this.router.events.pipe(
      filter(
        (e) =>
          e instanceof NavigationStart ||
          e instanceof NavigationEnd ||
          e instanceof NavigationCancel ||
          e instanceof NavigationError,
      ),
      map((e) => e instanceof NavigationStart),
    ),
    { initialValue: false },
  );

  // App scrolls inside <main class="shell__content">, not the window — so
  // Angular's built-in scrollPositionRestoration can't reach it. Reset the
  // scroll container to top on every route change.
  private readonly scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');

  constructor() {
    // Reset the in-app scroll container to top on every route change (the app
    // scrolls inside <main class="shell__content">, not the window).
    effect(() => {
      this.currentUrl();
      this.scrollContainer()?.nativeElement.scrollTo({ top: 0, left: 0 });
    });
  }

  isTabActive = (tab: NavTab): boolean => {
    const url = this.currentUrl();
    if (tab.exact) return url === tab.route;
    if (url === tab.route || url.startsWith(tab.route + '/')) return true;
    return (tab.activePrefixes ?? []).some(
      (p) => url === p || url.startsWith(p + '/'),
    );
  };

  readonly displayName = computed(() => this.auth.displayName() ?? 'User');
  readonly photoURL = computed(() => this.auth.photoURL());
  readonly practiceName = computed(() => this.auth.practiceName() ?? '');

  readonly canSeePracticeSettings = computed(() =>
    this.perms.hasAny([
      'practice.view_settings',
      'branches.view',
      'billing.manage',
    ]),
  );
  readonly canSeeTeam = computed(() =>
    this.perms.hasAny(['members.view', 'members.approve_requests']),
  );
  readonly canSeeLibrary = computed(() =>
    this.perms.hasAny([
      'medications.view',
      'diagnoses.view',
      'discharge_questions.view',
      'practice.view_settings',
    ]),
  );
  readonly canSeeAuditLog = computed(
    () => this.featureFlags.isEnabled('activity_log') && this.perms.has('audit.view'),
  );
  readonly canSeeSubscription = computed(() => this.perms.has('billing.manage'));
  readonly hasAnyPracticeMenu = computed(
    () =>
      this.canSeePracticeSettings() ||
      this.canSeeTeam() ||
      this.canSeeLibrary() ||
      this.canSeeAuditLog() ||
      this.canSeeSubscription(),
  );

  trackByRoute(_: number, tab: { route: string }): string {
    return tab.route;
  }

  onTabTap(tab: NavTab): void {
    this.router.navigate([tab.route]);
  }

  openSettings(): void {
    this.router.navigate(['/settings']);
  }

  openPracticeSettings(): void {
    this.router.navigate(['/practice-settings']);
  }

  openTeam(): void {
    this.router.navigate(['/team']);
  }

  openLibrary(): void {
    this.router.navigate(['/library']);
  }

  openAuditLog(): void {
    this.router.navigate(['/audit-log']);
  }

  openManageSubscription(): void {
    this.subscriptions.openManageUrl();
  }

  async switchPractice(): Promise<void> {
    await this.auth.clearPractice();
    this.router.navigate(['/practice-selection']);
  }

  async signOut(): Promise<void> {
    await this.auth.logout();
    this.router.navigate(['/login']);
  }
}
